import { Phone, PhoneOff, Video } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

interface IncomingCallModalProps {
  isOpen: boolean;
  callerName: string;
  callerAvatar: string | null;
  isVideo: boolean;
  onAnswer: () => void;
  onReject: () => void;
}

export function IncomingCallModal({
  isOpen,
  callerName,
  callerAvatar,
  isVideo,
  onAnswer,
  onReject,
}: IncomingCallModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-md"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="flex flex-col items-center text-center p-8"
          >
            {/* Animated rings behind avatar */}
            <div className="relative mb-8">
              <motion.div
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 rounded-full bg-primary/20"
                style={{ width: 160, height: 160, top: -20, left: -20 }}
              />
              <motion.div
                animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0, 0.3] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                className="absolute inset-0 rounded-full bg-primary/10"
                style={{ width: 200, height: 200, top: -40, left: -40 }}
              />
              <Avatar className="h-32 w-32 border-4 border-primary/50 shadow-xl">
                <AvatarImage src={callerAvatar || undefined} alt={callerName} />
                <AvatarFallback className="text-4xl bg-primary/20 text-primary">
                  {callerName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Caller info */}
            <h2 className="text-2xl font-bold text-foreground mb-2">{callerName}</h2>
            <p className="text-muted-foreground mb-2">
              Incoming {isVideo ? 'Video' : 'Voice'} Call
            </p>
            <div className="flex items-center gap-2 text-sm text-green-500 mb-8">
              <span className="flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="font-medium">FREE elefa Connect Call</span>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-8">
              {/* Reject */}
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="destructive"
                  size="icon"
                  className="h-16 w-16 rounded-full shadow-lg"
                  onClick={onReject}
                >
                  <PhoneOff className="h-7 w-7" />
                </Button>
                <p className="text-sm text-muted-foreground mt-2">Decline</p>
              </motion.div>

              {/* Answer */}
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  size="icon"
                  className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600 shadow-lg"
                  onClick={onAnswer}
                >
                  {isVideo ? <Video className="h-7 w-7" /> : <Phone className="h-7 w-7" />}
                </Button>
                <p className="text-sm text-muted-foreground mt-2">Answer</p>
              </motion.div>
            </div>

            {/* Encryption badge */}
            <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span>End-to-end encrypted</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
